import { TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '@/hooks/useCurrency';
import { formatUiNumber } from '@/utils/uiLocale';

export function formatVariationPct(v: number | null | undefined): string {
  if (v == null) return '—';
  const sign = v > 0 ? '+' : '';
  return `${sign}${(v * 100).toFixed(1)}%`;
}

export function variationBadgeClass(v: number | null | undefined): string {
  if (v == null) return 'bg-gray-50 text-gray-500';
  const abs = Math.abs(v);
  if (abs >= 0.2) return 'bg-red-100 text-red-800';
  if (abs >= 0.1) return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
}

export function VariationBadge({ value }: { value: number | null | undefined }) {
  if (value == null) return <span className="text-gray-400">—</span>;
  const Icon = value > 0 ? TrendingUp : value < 0 ? TrendingDown : null;
  return (
    <span
      className={`inline-flex items-center gap-0.5 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${variationBadgeClass(value)}`}
    >
      {Icon ? <Icon className="h-3 w-3 shrink-0 opacity-80" /> : null}
      {formatVariationPct(value)}
    </span>
  );
}

export function severityBadgeClass(severity: string): string {
  const s = severity.toLowerCase();
  if (s.includes('critical') || s === 'red') return 'bg-red-100 text-red-800';
  if (s.includes('warning') || s === 'yellow') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
}

export function SeverityBadge({ severity }: { severity: string | null | undefined }) {
  const { t } = useTranslation();
  if (!severity) return <span className="text-gray-400">—</span>;
  const key = `forecastReview.comparison.severityLabels.${severity.toLowerCase()}`;
  const label = t(key);
  const display = label !== key ? label : severity;
  return (
    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium capitalize ${severityBadgeClass(severity)}`}>
      {display}
    </span>
  );
}

function abcBadgeClass(abc: string): string {
  if (abc === 'A') return 'bg-emerald-100 text-emerald-800';
  if (abc === 'B') return 'bg-amber-100 text-amber-800';
  if (abc === 'C') return 'bg-gray-100 text-gray-700';
  return 'bg-gray-100 text-gray-600';
}

function xyzBadgeClass(xyz: string): string {
  if (xyz === 'X') return 'bg-blue-100 text-blue-800';
  if (xyz === 'Y') return 'bg-violet-100 text-violet-800';
  if (xyz === 'Z') return 'bg-orange-100 text-orange-800';
  return 'bg-gray-100 text-gray-600';
}

export type ForecastReviewClassificationLike = {
  abc_quantity?: string | null;
  abc_value?: string | null;
  xyz_class?: string | null;
  adi_cv_class?: string | null;
};

export function ClassificationBadges({
  classification,
}: {
  classification?: ForecastReviewClassificationLike | null;
}) {
  const { t } = useTranslation();
  const abc = classification?.abc_quantity;
  const xyz = classification?.xyz_class;
  if (!abc && !xyz) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex items-center gap-1">
      {abc ? (
        <span
          className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${abcBadgeClass(abc)}`}
          title={
            classification?.abc_value
              ? t('forecastReview.comparison.abcValueTooltip', { value: classification.abc_value })
              : undefined
          }
        >
          {abc}
        </span>
      ) : null}
      {abc && xyz ? <span className="text-xs text-gray-400">·</span> : null}
      {xyz ? (
        <span className={`inline-flex rounded px-1.5 py-0.5 text-xs font-semibold ${xyzBadgeClass(xyz)}`}>
          {xyz}
        </span>
      ) : null}
    </div>
  );
}

export function ColumnHeader({
  title,
  subtitle,
  align = 'right',
}: {
  title: string;
  subtitle?: string;
  align?: 'left' | 'right' | 'center';
}) {
  const alignClass =
    align === 'right' ? 'items-end text-right' : align === 'center' ? 'items-center text-center' : 'items-start text-left';
  return (
    <div className={`flex flex-col normal-case ${alignClass}`}>
      <span>{title}</span>
      {subtitle ? (
        <span className="mt-0.5 font-normal normal-case text-[10px] leading-tight text-gray-400">{subtitle}</span>
      ) : null}
    </div>
  );
}

export function SalesWithTrendCell({
  total,
  trend,
  viewType,
  trendTitle,
}: {
  total: number;
  trend?: Array<{ period: string; value: number }>;
  viewType: 'units' | 'value';
  trendTitle?: string;
}) {
  const { formatViewAmount } = useCurrency();
  const amount =
    total == null
      ? '—'
      : formatViewAmount(total, viewType);

  return (
    <div className="flex min-w-[5.5rem] flex-col items-end gap-1 overflow-visible">
      <span className="font-medium tabular-nums text-gray-800">{amount}</span>
      <SalesTrendSparkline points={trend} title={trendTitle} />
    </div>
  );
}

export function DeviationCell({
  diffQty,
  variationPct,
  viewType,
}: {
  diffQty: number | null | undefined;
  variationPct: number | null | undefined;
  viewType: 'units' | 'value';
}) {
  const { i18n } = useTranslation();
  const { formatViewAmount } = useCurrency();
  const diffLabel =
    diffQty == null
      ? '—'
      : (() => {
          if (viewType === 'value') {
            const formatted = formatViewAmount(Math.abs(diffQty), viewType);
            return diffQty > 0 ? `+${formatted}` : diffQty < 0 ? `-${formatted}` : formatted;
          }
          return `${diffQty > 0 ? '+' : ''}${formatUiNumber(diffQty, { maximumFractionDigits: 0 }, i18n.language)}`;
        })();

  return (
    <div className="flex min-w-[4.5rem] flex-col items-end gap-0.5">
      <span className="text-xs tabular-nums text-gray-700">{diffLabel}</span>
      <VariationBadge value={variationPct} />
    </div>
  );
}

export function SalesTrendSparkline({
  points,
  title,
}: {
  points?: Array<{ period: string; value: number }>;
  title?: string;
}) {
  if (!points?.length) return <span className="text-gray-400">—</span>;

  const values = points.map((p) => p.value);
  const hasData = values.some((v) => v > 0);
  if (!hasData) {
    return <span className="text-[10px] tabular-nums text-gray-400">0</span>;
  }

  const width = 84;
  const height = 26;
  const padding = 3;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || max || 1;

  const coords = values.map((v, i) => {
    const x =
      values.length === 1
        ? width / 2
        : padding + (i / (values.length - 1)) * (width - padding * 2);
    const y = padding + (height - padding * 2) * (1 - (v - min) / range);
    return { x, y };
  });

  const polylinePoints = coords.map((c) => `${c.x},${c.y}`).join(' ');
  const last = values[values.length - 1] ?? 0;
  const first = values[0] ?? 0;
  const strokeClass = last > first ? 'stroke-[#1681ba]' : last < first ? 'stroke-amber-600' : 'stroke-gray-500';

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      className="block shrink-0 overflow-visible"
      role="img"
      aria-label={title}
    >
      <title>{title}</title>
      <polyline
        fill="none"
        className={strokeClass}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        points={polylinePoints}
      />
      {coords.map((c, i) => (
        <circle
          key={points[i]?.period ?? i}
          cx={c.x}
          cy={c.y}
          r={values.length === 1 ? 2.5 : 1.75}
          className={strokeClass.replace('stroke-', 'fill-')}
        />
      ))}
    </svg>
  );
}
